/**
 * Bookings Cache Service
 * Manages offline caching and real-time sync of bookings data
 */

import { subscribeToCollection, getDocuments } from '../db_calls';
import { getCollectionPath } from '../utils/dbPaths';
import { useBookings } from '../stores';

// Helper function to capitalize names
const capitalizeName = (name) => {
  if (!name) return "";
  return name
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

// localStorage caching removed - using Firestore persistent cache only

/**
 * Initialize bookings cache system
 * Sets up real-time listener and loads from cache if offline
 * @returns {Function} Unsubscribe function
 */
export const initializeBookingsCache = () => {

  // Set up real-time subscription with tenant/store path
  const collectionPath = getCollectionPath('bookings');
  console.log(`📡 Setting up bookings subscription for path: ${collectionPath}`);
  const unsubscribe = subscribeToCollection(
    collectionPath,
    (masterBookings, error) => {
      if (error) {
        console.error('❌ Error in bookings subscription:', error);
        console.error('❌ Error in bookings subscription:', error);
        return;
      }

      // Process bookings - add computed fullName to each booking
      const processedBookings = masterBookings.map((booking) => ({
        ...booking,
        fullName: `${capitalizeName(booking.firstName || "")} ${capitalizeName(booking.lastName || "")}`.trim(),
      }));
      
      // Update Zustand store
      const setBookings = useBookings.getState().setBookings;
      setBookings(processedBookings);
      
    },
    {
      orderBy: {
        field: 'createdAt',
        direction: 'desc',
      },
    }
  );

  return unsubscribe;
};

/**
 * Get all bookings from cache (synchronous)
 * @returns {Array} Array of booking objects
 */
export const getBookingsFromCache = () => {
  try {
    const bookings = useBookings.getState().bookings;
    return bookings;
  } catch (error) {
    console.error('❌ Error reading bookings from cache:', error);
    return [];
  }
};

/**
 * Get a single booking by ID from cache
 * @param {string} bookingId - Booking ID
 * @returns {Object|null} Booking object or null if not found
 */
export const getBookingFromCache = (bookingId) => {
  const bookings = getBookingsFromCache();
  const booking = bookings.find(r => r.id === bookingId || r.bookingId === bookingId);
  return booking || null;
};

/**
 * Get bookings by phone number from cache
 * @param {string} phone - Customer phone number
 * @returns {Array} Array of booking objects
 */
export const getBookingsByPhoneFromCache = (phone) => {
  const bookings = getBookingsFromCache();
  return bookings.filter(r => r.customerPhone === phone || r.phone === phone);
};

// Helper: Parse Firestore timestamp to Date
const parseFirestoreTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp.seconds) {
    return new Date(timestamp.seconds * 1000);
  }
  return null;
};

/**
 * Get today's bookings from cache
 * @returns {Array} Array of booking objects for today
 */
export const getTodaysBookingsFromCache = () => {
  const bookings = getBookingsFromCache();
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const todayMillis = today.getTime();
  
  const tomorrowMillis = todayMillis + 24 * 60 * 60 * 1000;

  return bookings.filter((booking) => {
    // Check if booking starts or ends today
    const startDate = parseFirestoreTimestamp(booking.startDate);
    const endDate = parseFirestoreTimestamp(booking.endDate);

    if (startDate) {
      const startMillis = startDate.getTime();
      if (startMillis >= todayMillis && startMillis < tomorrowMillis) {
        return true;
      }
    }

    if (endDate) {
      const endMillis = endDate.getTime();
      if (endMillis >= todayMillis && endMillis < tomorrowMillis) {
        return true;
      }
    }

    // Check if booking is currently active (started before today, ends after today)
    if (startDate && endDate) {
      const startMillis = startDate.getTime();
      const endMillis = endDate.getTime();
      if (startMillis < todayMillis && endMillis >= todayMillis) {
        return true; // Active booking
      }
    }

    return false;
  });
};

// localStorage caching removed - using Firestore persistent cache only
// getBookingsFromCache() now reads from Zustand store which is updated by Firestore subscriptions

/**
 * Force refresh bookings from server
 * @returns {Promise<Array>} Array of bookings
 */
export const refreshBookingsCache = async () => {
  
  try {
    const collectionPath = getCollectionPath('bookings');
    const bookings = await getDocuments(collectionPath, {
      orderBy: {
        field: 'createdAt',
        direction: 'desc',
      },
    });
    
    // Add computed fullName to each booking (with capitalized names)
    const bookingsWithFullName = bookings.map((booking) => ({
      ...booking,
      fullName: `${capitalizeName(booking.firstName || "")} ${capitalizeName(booking.lastName || "")}`.trim(),
    }));
    
    // Update Zustand store
    const setBookings = useBookings.getState().setBookings;
    setBookings(bookingsWithFullName);
    return bookingsWithFullName;
  } catch (error) {
    console.error('❌ Error refreshing bookings cache:', error);
    // Return from Zustand store as fallback
    return useBookings.getState().bookings;
  }
};

/**
 * Update a single booking in cache
 * @param {string} bookingId - Booking document ID
 * @param {Object} updates - Fields to update
 */
export const updateBookingInCache = (bookingId, updates) => {
  try {
    const bookings = useBookings.getState().bookings;
    const index = bookings.findIndex(r => r.id === bookingId);
    
    if (index !== -1) {
      const updatedBookings = [...bookings];
      updatedBookings[index] = { ...updatedBookings[index], ...updates };
      useBookings.getState().setBookings(updatedBookings);
    }
  } catch (error) {
    console.error('❌ Error updating booking in cache:', error);
  }
};

/**
 * Add a new booking to cache
 * @param {Object} booking - Booking object
 */
export const addBookingToCache = (booking) => {
  try {
    const bookings = getBookingsFromCache();
    
    // Check if booking already exists
    const existingIndex = bookings.findIndex(r => r.id === booking.id);
    
    if (existingIndex !== -1) {
      // Update existing booking
      bookings[existingIndex] = booking;
    } else {
      // Add new booking
      bookings.push(booking);
    }
    
    // Update Zustand store
    useBookings.getState().setBookings(bookings);
  } catch (error) {
    console.error('❌ Error adding booking to cache:', error);
  }
};

/**
 * Remove a booking from cache
 * @param {string} bookingId - Booking document ID
 */
export const removeBookingFromCache = (bookingId) => {
  try {
    const bookings = getBookingsFromCache();
    const filtered = bookings.filter(r => r.id !== bookingId);
    
    if (filtered.length < bookings.length) {
      // Update Zustand store
      useBookings.getState().setBookings(filtered);
    }
  } catch (error) {
    console.error('❌ Error removing booking from cache:', error);
  }
};

/**
 * Clear all bookings from cache
 */
export const clearBookingsCache = () => {
  try {
    useBookings.getState().clearBookings();
  } catch (error) {
    console.error('❌ Error clearing bookings cache:', error);
  }
};

/**
 * Get cache statistics
 * @returns {Object} Cache stats
 */
export const getBookingsCacheStats = () => {
  const bookings = getBookingsFromCache();
  
  return {
    count: bookings.length,
    lastUpdated: null, // No longer tracking timestamp - using Firestore cache
    todaysBookings: getTodaysBookingsFromCache().length,
    confirmedBookings: bookings.filter(r => r.status === 'confirmed').length,
    pendingBookings: bookings.filter(r => r.status === 'pending').length,
  };
};

